import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

export interface ReservationPackage {
  id: string;
  title: string;
  description: string;
  price: number;
  maxGuests: number;
  features: string[];
  active: boolean;
  image?: string;
  createdAt?: any;
}

export type ReservationStatus = 'pendiente' | 'confirmada' | 'completada' | 'cancelada';

export interface Reservation {
  id: string;
  reservationCode: string;
  packageId?: string | null;
  packageTitle?: string | null;
  packagePrice?: number;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  guestCount: number;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  notes?: string;
  state: ReservationStatus;
  createdAt: any;
  userId?: string;
  restaurantId: string;
}

// ── SUSCRIPCIONES Y METODOS DE RESERVAS ────────────────────────

export function subscribeToReservations(
  restaurantId: string,
  callback: (reservations: Reservation[]) => void
) {
  const colRef = collection(db, 'restaurants', restaurantId, 'reservations');
  const q = query(colRef, orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const items: Reservation[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Reservation, 'id'>),
      }));
      callback(items);
    },
    (error) => {
      console.error('Error suscribiendo a reservas:', error);
      // Fallback sin orderBy por si falta índice
      onSnapshot(colRef, (snap) => {
        const items: Reservation[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Reservation, 'id'>),
        }));
        callback(items);
      });
    }
  );
}

export async function updateReservationStatus(
  restaurantId: string,
  reservationId: string,
  state: ReservationStatus
) {
  const docRef = doc(db, 'restaurants', restaurantId, 'reservations', reservationId);
  await updateDoc(docRef, { state });
}

export async function createReservationAdmin(
  restaurantId: string,
  data: Omit<Reservation, 'id' | 'createdAt' | 'restaurantId'>
) {
  const colRef = collection(db, 'restaurants', restaurantId, 'reservations');
  await addDoc(colRef, {
    ...data,
    restaurantId,
    createdAt: serverTimestamp(),
  });
}

export async function deleteReservation(
  restaurantId: string,
  reservationId: string
) {
  const docRef = doc(db, 'restaurants', restaurantId, 'reservations', reservationId);
  await deleteDoc(docRef);
}

// ── SUSCRIPCIONES Y METODOS DE PAQUETES ────────────────────────

export function subscribeToReservationPackagesAdmin(
  restaurantId: string,
  callback: (packages: ReservationPackage[]) => void
) {
  const colRef = collection(db, 'restaurants', restaurantId, 'reservationPackages');
  return onSnapshot(colRef, (snapshot) => {
    const items: ReservationPackage[] = snapshot.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<ReservationPackage, 'id'>),
    }));
    callback(items);
  });
}

export async function createReservationPackageAdmin(
  restaurantId: string,
  pkg: Omit<ReservationPackage, 'id' | 'createdAt'>
) {
  const colRef = collection(db, 'restaurants', restaurantId, 'reservationPackages');
  await addDoc(colRef, {
    ...pkg,
    createdAt: serverTimestamp(),
  });
}

export async function updateReservationPackageAdmin(
  restaurantId: string,
  pkgId: string,
  pkg: Partial<ReservationPackage>
) {
  const docRef = doc(db, 'restaurants', restaurantId, 'reservationPackages', pkgId);
  await updateDoc(docRef, pkg);
}

export async function deleteReservationPackageAdmin(
  restaurantId: string,
  pkgId: string
) {
  const docRef = doc(db, 'restaurants', restaurantId, 'reservationPackages', pkgId);
  await deleteDoc(docRef);
}
